import { useState, useEffect } from "react";
import type { StatusGridResponse, ServiceStatus, RegionStatus, ServiceGridItem, ComponentGridItem } from "../../types/status";
import { adminFetch } from "../../lib/api";
import { apiFetch } from "../../lib/config";

const STATUS_OPTIONS: ServiceStatus[] = ["operational", "degraded", "outage", "maintenance"];

const STATUS_LABEL: Record<ServiceStatus, string> = {
  operational: "Operational",
  degraded: "Degraded",
  outage: "Outage",
  maintenance: "Maintenance",
};

interface OverrideModal {
  serviceRegionId: string;
  label: string;
  regionName: string;
  currentStatus: ServiceStatus;
}

function StatusCell({ status, onClick }: { status: ServiceStatus; onClick: () => void }) {
  const cls = {
    operational: "ac-badge ac-badge--operational",
    degraded: "ac-badge ac-badge--degraded",
    outage: "ac-badge ac-badge--outage",
    maintenance: "ac-badge ac-badge--maintenance",
  }[status];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ background: "none", border: "none", cursor: "pointer", padding: "0.25rem", borderRadius: "4px" }}
      title="Click to override"
    >
      <span className={cls}>{STATUS_LABEL[status]}</span>
    </button>
  );
}

function EmptyCell() {
  return <span style={{ color: "var(--aptean-text-muted)", fontSize: "0.75rem" }}>—</span>;
}

/** Collect all unique region slugs → name across the whole grid */
function collectRegions(services: ServiceGridItem[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const svc of services) {
    for (const r of svc.regions) map.set(r.region_slug, r.region_name);
    for (const comp of svc.components) {
      for (const r of comp.regions) map.set(r.region_slug, r.region_name);
    }
  }
  return map;
}

/** All RegionStatus objects from the whole grid (for summary counts) */
function allRegionStatuses(services: ServiceGridItem[]): RegionStatus[] {
  return services.flatMap(svc => [
    ...svc.regions,
    ...svc.components.flatMap(c => c.regions),
  ]);
}

export function StatusManagementPage() {
  const [grid, setGrid] = useState<StatusGridResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<OverrideModal | null>(null);
  const [overrideStatus, setOverrideStatus] = useState<ServiceStatus>("degraded");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function loadGrid() {
    const r = await apiFetch("/api/v1/status/");
    if (r.ok) setGrid(await r.json());
    setLoading(false);
  }

  useEffect(() => { loadGrid(); }, []);

  function openModal(label: string, region: RegionStatus) {
    setModal({
      serviceRegionId: region.service_region_id,
      label,
      regionName: region.region_name,
      currentStatus: region.status,
    });
    setOverrideStatus(region.status === "operational" ? "degraded" : region.status);
    setReason("");
    setError("");
  }

  async function submitOverride(e: React.FormEvent) {
    e.preventDefault();
    if (!modal) return;
    if (!reason.trim()) { setError("Reason is required."); return; }
    setSubmitting(true); setError("");
    try {
      const r = await adminFetch(`/api/v1/admin/service-regions/${modal.serviceRegionId}/status/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: overrideStatus, reason }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setModal(null);
      await loadGrid();
    } catch {
      setError("Override failed. Please try again.");
    } finally { setSubmitting(false); }
  }

  async function clearOverride() {
    if (!modal) return;
    setSubmitting(true); setError("");
    try {
      const r = await adminFetch(`/api/v1/admin/service-regions/${modal.serviceRegionId}/status/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: null, reason: "" }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setModal(null);
      await loadGrid();
    } catch {
      setError("Failed to clear override.");
    } finally { setSubmitting(false); }
  }

  const services = grid?.services ?? [];
  const regionMap = collectRegions(services);
  const regionSlugs = [...regionMap.keys()].sort((a, b) =>
    (regionMap.get(a) ?? a).localeCompare(regionMap.get(b) ?? b)
  );
  const allStatuses = allRegionStatuses(services);
  const counts = {
    operational: allStatuses.filter(r => r.status === "operational").length,
    degraded: allStatuses.filter(r => r.status === "degraded").length,
    outage: allStatuses.filter(r => r.status === "outage").length,
    maintenance: allStatuses.filter(r => r.status === "maintenance").length,
  };

  function regionLookup(regions: RegionStatus[]) {
    return Object.fromEntries(regions.map(r => [r.region_slug, r]));
  }

  function renderComponentRow(comp: ComponentGridItem, svcName: string) {
    const lookup = regionLookup(comp.regions);
    const label = `${svcName} / ${comp.name}`;
    return (
      <tr key={comp.id}>
        <td style={{ paddingLeft: "1.75rem" }}>
          <div className="ac-row-title" style={{ fontSize: "0.875rem" }}>{comp.name}</div>
          <div className="ac-row-sub">{comp.slug}</div>
        </td>
        {regionSlugs.map(slug => {
          const r = lookup[slug];
          return (
            <td key={slug} style={{ textAlign: "center" }}>
              {r ? <StatusCell status={r.status} onClick={() => openModal(label, r)} /> : <EmptyCell />}
            </td>
          );
        })}
      </tr>
    );
  }

  function renderDirectRow(svc: ServiceGridItem) {
    const lookup = regionLookup(svc.regions);
    return (
      <tr key={svc.id}>
        <td>
          <div className="ac-row-title">{svc.name}</div>
          <div className="ac-row-sub">{svc.slug}</div>
        </td>
        {regionSlugs.map(slug => {
          const r = lookup[slug];
          return (
            <td key={slug} style={{ textAlign: "center" }}>
              {r ? <StatusCell status={r.status} onClick={() => openModal(svc.name, r)} /> : <EmptyCell />}
            </td>
          );
        })}
      </tr>
    );
  }

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Status Management</h1>
          <p className="ac-page-subtitle">View and override per-component-region status across all deployment regions</p>
        </div>
        <div className="ac-page-header__actions">
          <button type="button" className="aptean-btn aptean-btn--secondary" onClick={loadGrid}>Refresh</button>
        </div>
      </div>

      {grid && (
        <div className="ac-summary-strip">
          <div className="ac-summary-card">
            <div className="ac-summary-card__value">{allStatuses.length}</div>
            <div className="ac-summary-card__label">Service Regions</div>
          </div>
          <div className={`ac-summary-card${counts.operational === allStatuses.length && allStatuses.length > 0 ? " ac-summary-card--ok" : ""}`}>
            <div className="ac-summary-card__value">{counts.operational}</div>
            <div className="ac-summary-card__label">Operational</div>
          </div>
          <div className={`ac-summary-card${counts.degraded > 0 ? " ac-summary-card--warn" : ""}`}>
            <div className="ac-summary-card__value">{counts.degraded}</div>
            <div className="ac-summary-card__label">Degraded</div>
          </div>
          <div className={`ac-summary-card${counts.outage > 0 ? " ac-summary-card--danger" : ""}`}>
            <div className="ac-summary-card__value">{counts.outage}</div>
            <div className="ac-summary-card__label">Outage</div>
          </div>
          <div className={`ac-summary-card${counts.maintenance > 0 ? " ac-summary-card--accent" : ""}`}>
            <div className="ac-summary-card__value">{counts.maintenance}</div>
            <div className="ac-summary-card__label">Maintenance</div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding: "3rem", textAlign: "center", color: "var(--aptean-text-secondary)" }}>Loading...</div>
      ) : !grid || services.length === 0 ? (
        <div className="ac-table-wrap">
          <table className="ac-table"><tbody><tr><td>
            <div className="ac-empty">
              <div className="ac-empty__title">No services configured</div>
              <div className="ac-empty__body">Add services and components in the Service Registry to manage their status here.</div>
            </div>
          </td></tr></tbody></table>
        </div>
      ) : (
        <div className="ac-table-wrap" style={{ overflowX: "auto" }}>
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ minWidth: "200px" }}>Service / Component</th>
                {regionSlugs.map(slug => (
                  <th key={slug} style={{ textAlign: "center", minWidth: "130px" }}>
                    {regionMap.get(slug)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.map(svc => {
                const hasComponents = svc.components.length > 0;
                const hasDirectRegions = svc.regions.length > 0;
                return (
                  <>
                    {/* Service header row when it has components */}
                    {hasComponents && (
                      <tr key={`${svc.id}-header`} style={{ background: "var(--aptean-bg-overlay)" }}>
                        <td colSpan={regionSlugs.length + 1} style={{ fontWeight: 700, fontSize: "0.875rem", padding: "0.5rem 0.75rem", color: "var(--aptean-text-primary)" }}>
                          {svc.name}
                        </td>
                      </tr>
                    )}
                    {/* Component rows */}
                    {svc.components.map(comp => renderComponentRow(comp, svc.name))}
                    {/* Legacy direct-region row */}
                    {hasDirectRegions && !hasComponents && renderDirectRow(svc)}
                  </>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: "0.625rem 1rem", fontSize: "0.75rem", color: "var(--aptean-text-secondary)", borderTop: "1px solid var(--aptean-border)" }}>
            Click any status badge to override it. Overrides are applied immediately.
          </div>
        </div>
      )}

      {modal && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Override Status</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setModal(null)}>&#10005;</button>
            </div>
            <form onSubmit={submitOverride}>
              <div className="ac-dialog__body">
                <div style={{ marginBottom: "1rem", padding: "0.625rem 0.875rem", background: "var(--aptean-bg-overlay)", borderRadius: "6px", fontSize: "0.875rem" }}>
                  <div><span style={{ color: "var(--aptean-text-secondary)" }}>Component: </span><strong>{modal.label}</strong></div>
                  <div style={{ marginTop: "0.25rem" }}><span style={{ color: "var(--aptean-text-secondary)" }}>Region: </span><strong>{modal.regionName}</strong></div>
                  <div style={{ marginTop: "0.375rem" }}>
                    <span style={{ color: "var(--aptean-text-secondary)" }}>Current: </span>
                    <span className={`ac-badge ac-badge--${modal.currentStatus}`}>{STATUS_LABEL[modal.currentStatus]}</span>
                  </div>
                </div>
                {error && <div className="ac-alert ac-alert--error">{error}</div>}
                <div className="ac-field">
                  <label htmlFor="override-status">Override Status</label>
                  <select id="override-status" className="aptean-input" value={overrideStatus} onChange={e => setOverrideStatus(e.target.value as ServiceStatus)}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
                <div className="ac-field">
                  <label htmlFor="override-reason">Reason *</label>
                  <input id="override-reason" type="text" className="aptean-input" value={reason}
                    onChange={e => setReason(e.target.value)} placeholder="e.g. Elevated error rate in EU-West" required />
                </div>
              </div>
              <div className="ac-dialog__footer">
                {modal.currentStatus !== "operational" && (
                  <button type="button" className="aptean-btn aptean-btn--ghost" onClick={clearOverride} disabled={submitting} style={{ marginRight: "auto" }}>
                    Clear Override
                  </button>
                )}
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={submitting}>
                  {submitting ? "Applying..." : "Apply Override"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
